import type { TemplatePaletteConfig } from 'src/modules/designStudio/helpers/data/PaletteStyles';
import type { LegacyDesignConfig } from 'src/modules/designStudio/types';

/**
 * This class provides type definitions and util methods to deal with the design sub-system of machete's feature api.
 * This class was introduced as part of the effort to refactor the data format used by design studio to store changes to the model.
 * For details on the refactor please refer to this document:
 * - https://hoverinc.atlassian.net/wiki/spaces/EPD/pages/2887483397/Design+Studio+Data+Format+Refactor+50+80+110
 * This file helps implement the 80% solution laid out in the proposal.
 *
 */

export enum DoorGroup {
  FRONT = 'FRONT',
  SERVICE = 'SERVICE',
  GARAGE = 'GARAGE',
  SLIDING_GLASS = 'SLIDING_GLASS',
}

export const PRIMARY_TEMPLATE_NAME = 'primary-template';

/**
 * Most of these types are here as a temporary measure to expose the types of the design config used in the design sub-system of the feature api.
 * TODO: Remove and consume types from machete: https://hoverinc.atlassian.net/browse/TDP-5280
 */
export type Structure = {
  designName?: string;
  wallStyles?: {
    name?: string;
    policy: WallStylePolicy;
    appearance: TemplateWallAppearance;
    trims?: {
      name?: string;
      policy: TrimStylePolicy;
      appearance: TrimAppearance;
    }[];
  }[];
  roofStyles?: {
    policy: {
      onAppearances?: TemplateRoofAppearance[];
    };
    appearance: TemplateRoofAppearance;
  }[];
  windowStyles?: {
    policy: {
      onAppearances?: TemplateWindowAppearance[];
    };
    appearance: TemplateWindowAppearance;
  }[];
  trimStyles?: {
    policy: {
      onAppearances?: TemplateTrimAppearance[];
    };
    appearance: TemplateTrimAppearance;
  }[];
  doorStyles?: DoorStyle[];
  shutterStyles?: {
    policy: {
      onCurrentShutters: 'remove' | 'replace' | 'remain';
    };
  }[];
};

export type DoorStylePolicy = {
  onLabels?: number[];
  isFacing?: string[];
  onSide?: string[];
  onAspectRatio?: { ratio: number; tol: number };
};

export type DoorStyle = {
  name?: string;
  policy: DoorStylePolicy;
  appearance: TextureAppearance;
};

export type WallStructureConditions = {
  hasWindows?: boolean;
  hasDoors?: boolean;
  spansMultipleFloors?: boolean;
  topFloor?: boolean;
};

export type PartitionIntersection = {
  usesConstraints: Partition[];
};

export enum PartitionGuideline {
  GABLE_CUT = 'gable_cut',
}

export enum PartitionSide {
  ABOVE = 'above',
  BELOW = 'below',
}

export type Partition = {
  cutAlong: PartitionGuideline;
  keepSide: PartitionSide;
};

export type TrimStylePolicy = {
  onOrientations?: EdgeOrientation[];
  onCorners?: EdgeGeometry[];
  withinFaces?: EdgePosition[];
  onAppearances?: TextureAppearance[];

  /**
   * if onFaces is defined,
   * the trim must lie on a face
   * that has a style using one of the listed names
   */
  onFaces?: string[];

  /**
   * if hasNeighbors is defined,
   * the face neighboring the owner face
   * must have one of the listed Designs applied to it.
   */
  hasNeighbors?: string[];
};

export type TrimAppearance = {
  texture: TextureAppearance;
  width: number;
};

export enum EdgeOrientation {
  HORIZONTAL = 'horizontal',
  SLOPED = 'sloped',
  VERTICAL = 'vertical',
}

export enum EdgeGeometry {
  CONVEX = 'convex',
  CONCAVE = 'concave',
  COPLANAR = 'coplanar',
}

export enum EdgePosition {
  EXTERIOR_TOP = 'exterior_top',
  EXTERIOR_BOTTOM = 'exterior_bottom',
  EXTERIOR_SIDE = 'exterior_side',

  INTERIOR_TOP = 'interior_top',
  INTERIOR_BOTTOM = 'interior_bottom',
  INTERIOR_SIDE = 'interior_side',
}

export enum WallDirection {
  FRONT = 'front',
  BACK = 'back',
  SIDE = 'side',
}

export type WallStylePolicy = {
  onAppearances?: TextureAppearance[];
  onLabels?: number[];
  onSides?: WallDirection[];
  isFacing?: WallDirection[];
  hasPartitions?: PartitionIntersection[];
  hasStructuralConditions?: WallStructureConditions[];
};

export type TextureAppearance = {
  variationUUID?: string;
  textureId?: number | null;
  color?: string | null;
};

export type NetworkAppearance = {
  url: string;
};

export type TemplateWallAppearance = TextureAppearance;

export type TemplateRoofAppearance = TextureAppearance;

export type TemplateTrimAppearance = {
  width?: number | null;
  texture?: {
    textureId?: number | null;
    color?: string | null;
  };
};

export type TemplateWindowAppearance = {
  basicParametricOptions?: {
    frameType: string | null;
    grilleType: string | null;
  };
  color?: string | null;
};

export type DesignBuilding = {
  designer: Designer;
};

export type Designer = {
  set_active_design: (config: Structure) => Promise<Design>;
  get_active_design: () => Design;
  view_design: (name: string) => void;
};

export type DoorCounts = {
  [DoorGroup.FRONT]: {
    count: number;
  };
  [DoorGroup.SERVICE]: {
    count: number;
  };
  [DoorGroup.GARAGE]: {
    count: number;
  };
  [DoorGroup.SLIDING_GLASS]: {
    count: number;
  };
};

export type DoorTexture = {
  textureId: number;
  url: string;
  width: number;
  height: number;
  canTile: boolean;
};

export type Design = {
  change_wall_appearances: (
    appearance_before: TemplateWallAppearance[],
    appearance_after: TemplateWallAppearance,
  ) => Promise<void>;
  get_wall_appearances: () => TemplateWallAppearance[];

  change_roof_appearances: (
    appearance_before: TemplateRoofAppearance[],
    appearance_after: TemplateRoofAppearance,
  ) => Promise<void>;
  get_roof_appearances: () => TemplateRoofAppearance[];

  change_trim_appearances: (
    appearance_before: TextureAppearance[],
    appearance_after: TextureAppearance,
  ) => Promise<void>;
  get_trim_appearances: () => TextureAppearance[];

  change_window_appearances: (
    appearance_before: TemplateWindowAppearance[],
    appearance_after: TemplateWindowAppearance,
  ) => Promise<void>;
  get_window_appearances: () => TemplateWindowAppearance[];

  to_config: () => Structure;
  apply_palette: (palette: TemplatePaletteConfig) => Promise<void>;
  get_door_counts: () => DoorCounts;
  change_front_doors: (
    textures: DoorTexture[],
    color: string | undefined,
    tolerance: number,
  ) => Promise<void>;
  change_garage_doors: (
    textures: DoorTexture[],
    color: string | undefined,
    tolerance: number,
  ) => Promise<void>;
  change_service_doors: (
    textures: DoorTexture[],
    color: string | undefined,
    tolerance: number,
  ) => Promise<void>;
};

/**
 * @description returns "empty" template design config. When applied to the building this config will show the original state
 * of the building. This config can be used to reset the appearance of the building to its original at load appearance.
 */
export const createEmptyTemplateDesignConfig = (): Structure => ({
  designName: PRIMARY_TEMPLATE_NAME,
  wallStyles: [],
  roofStyles: [],
  trimStyles: [],
  windowStyles: [],
});

/**
 * @description isTemplateDesignConfig returns true if the designConfig is of type Structure
 * This is useful during the transition of designConfig from LegacyDesignConfig -> Structure
 */
export function isTemplateDesignConfig(
  designConfig: any,
): designConfig is Structure {
  if (!designConfig) {
    return false;
  }

  return (designConfig as LegacyDesignConfig).ROOFS === undefined;
}

export function getCurrentDesignConfig(designer: Designer): Structure {
  const config = designer.get_active_design().to_config();
  /**
   * The main use of this function is to return the template config of the building. This config is saved to firebase
   * and used to save the state of the model. Firebase does not handle undefined values, so we remove all undefined values using
   * JSON.parse(JSON.stringify()).
   */
  return JSON.parse(JSON.stringify(config, undefined, 0)) as Structure;
}

export const parseDesignConfig = (
  config: LegacyDesignConfig | Structure,
): Structure =>
  isTemplateDesignConfig(config) ? config : createEmptyTemplateDesignConfig();
